import * as XLSX from 'xlsx';

export const REQUIRED_FIELDS = ['SectionName', 'CategoryName', 'Identifier'];
const SANITIZE_FIELDS = ['Identifier', 'Name', 'Description'];

const REQUIRED_HEADERS = [
  'SectionName',
  'CategoryName',
  'Identifier',
  'Name',
  'Description'
];

const OPTIONAL_HEADERS = [
  'Attribute-Tray Width',
  'Attribute-Tray Height',
  'Attribute-Tray Depth',
  'Attribute-Case Width',
  'Attribute-Case Height',
  'Attribute-Case Depth'
];

const TEMPLATE_HEADERS = [...REQUIRED_HEADERS, ...OPTIONAL_HEADERS];

export function validateTemplateFormat(headers) {
  const missingRequired = REQUIRED_HEADERS.filter(header => !headers.includes(header));
  const missingOptional = OPTIONAL_HEADERS.filter(header => !headers.includes(header));
  return {
    isValid: missingRequired.length === 0,
    missingRequired,
    missingOptional,
    missingHeaders: [...missingRequired, ...missingOptional],
    expectedHeaders: TEMPLATE_HEADERS
  };
}

/**
 * Fuzzy match configuration for brand name harmonisation.
 * Uses a flexible matching approach - any ONE of these conditions triggers a match:
 * 1. Standard match: low distance + high similarity + low relative distance
 * 2. Very high similarity (catches sub-brands)
 * 3. Very low distance with reasonable similarity (catches typos)
 * 4. One string contains the other (catches brand variants)
 */
const FUZZY_CONFIG = {
  // Standard match thresholds (all must pass)
  maxDistance: 2,
  minSimilarity: 0.85,
  maxRelativeDistance: 0.25,
  
  // Alternative match: very high similarity alone
  highSimilarityThreshold: 0.90,
  
  // Alternative match: low distance with relaxed similarity
  lowDistanceMaxDistance: 2,
  lowDistanceMinSimilarity: 0.75,
  lowDistanceMaxRelativeDistance: 0.30,
  
  // Contains match: minimum length for the shorter string
  containsMinLength: 4
};

/**
 * Damerau-Levenshtein distance
 * Counts insertions, deletions, substitutions, and adjacent transpositions.
 */
function damerauLevenshtein(a, b) {
  const lenA = a.length;
  const lenB = b.length;
  const cache = Array.from({ length: lenA + 1 }, () => new Array(lenB + 1).fill(0));

  for (let i = 0; i <= lenA; i++) cache[i][0] = i;
  for (let j = 0; j <= lenB; j++) cache[0][j] = j;

  for (let i = 1; i <= lenA; i++) {
    for (let j = 1; j <= lenB; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;

      cache[i][j] = Math.min(
        cache[i - 1][j] + 1,         
        cache[i][j - 1] + 1,         
        cache[i - 1][j - 1] + cost   
      );

      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        cache[i][j] = Math.min(cache[i][j], cache[i - 2][j - 2] + cost);
      }
    }
  }
  return cache[lenA][lenB];
}

/**
 * Jaro-Winkler similarity score.
 * Returns a value in [0, 1] where 1 is identical.
 */
function jaroWinkler(s1, s2) {
  if (s1 === s2) return 1;

  const len1 = s1.length;
  const len2 = s2.length;

  if (len1 === 0 || len2 === 0) return 0;

  const matchDistance = Math.floor(Math.max(len1, len2) / 2) - 1;
  const s1Matches = Array(len1).fill(false);
  const s2Matches = Array(len2).fill(false);

  let matches = 0;

  for (let i = 0; i < len1; i++) {
    const start = Math.max(0, i - matchDistance);
    const end = Math.min(i + matchDistance + 1, len2);

    for (let j = start; j < end; j++) {
      if (s2Matches[j]) continue;
      if (s1[i] !== s2[j]) continue;

      s1Matches[i] = true;
      s2Matches[j] = true;
      matches++;
      break;
    }
  }
  if (matches === 0) return 0;

  let transpositions = 0;
  let k = 0;
  for (let i = 0; i < len1; i++) {
    if (!s1Matches[i]) continue;
    while (!s2Matches[k]) k++;
    if (s1[i] !== s2[k]) transpositions++;
    k++;
  }

  const m = matches;
  const jaro = (m / len1 + m / len2 + (m - transpositions / 2) / m) / 3;

  let prefixLength = 0;
  const maxPrefix = 4;
  while (prefixLength < maxPrefix && prefixLength < len1 && prefixLength < len2 && s1[prefixLength] === s2[prefixLength]) {
    prefixLength++;
  }

  return jaro + prefixLength * 0.1 * (1 - jaro);
}

function normaliseForComparison(value) {
  if (!value) return '';
  return String(value).toLowerCase().replace(/[^a-z0-9]/g, '');
}


function areSimilar(str1, str2, config = FUZZY_CONFIG) {
  const norm1 = normaliseForComparison(str1);
  const norm2 = normaliseForComparison(str2);
  
  if (norm1 === norm2) return true;
  
  if (norm1.length === 0 || norm2.length === 0) return false;
  
  const distance = damerauLevenshtein(norm1, norm2);
  const similarity = jaroWinkler(norm1, norm2);
  const relativeDistance = distance / Math.max(norm1.length, norm2.length);
  const shorter = norm1.length <= norm2.length ? norm1 : norm2;
  const longer = norm1.length > norm2.length ? norm1 : norm2;
  
  // Check 1: Standard match (all conditions)
  const standardMatch = distance <= config.maxDistance && 
                        similarity >= config.minSimilarity && 
                        relativeDistance <= config.maxRelativeDistance;
  
  // Check 2: Very high similarity (catches sub-brands like LUCOZADE vs LUCOZADE ZERO)
  const highSimilarityMatch = similarity >= config.highSimilarityThreshold;
  
  // Check 3: Low distance with relaxed similarity (catches typos like BINSONS vs ROBINSONS)
  const lowDistanceMatch = distance <= config.lowDistanceMaxDistance && 
                           similarity >= config.lowDistanceMinSimilarity &&
                           relativeDistance <= config.lowDistanceMaxRelativeDistance;
  
  // Check 4: One contains the other (catches brand variants)
  const containsMatch = shorter.length >= config.containsMinLength && 
                        longer.includes(shorter);
  
  const passes = standardMatch || highSimilarityMatch || lowDistanceMatch || containsMatch;
  
  return passes;
}

export async function parseExcelFile(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
          defval: '',
          raw: false
        });
        
        const headers = jsonData.length > 0 ? Object.keys(jsonData[0]) : [];
        
        resolve({
          rows: jsonData,
          headers: headers,
          error: null
        });
      } catch (err) {
        resolve({
          rows: [],
          headers: [],
          error: `Failed to parse file: ${err.message}`
        });
      }
    };
    
    reader.onerror = () => {
      resolve({
        rows: [],
        headers: [],
        error: 'Failed to read file'
      });
    };
    
    reader.readAsArrayBuffer(file);
  });
}

function getAssetDetailId(row) {
  return String(row.AssetDetailid || row.AssetDetailId || row.assetdetailid || '').trim();
}

export function findDuplicates(rows) {
  const identifierGroups = {};
  
  rows.forEach((row, index) => {
    const identifier = String(row.Identifier || '').trim();
    if (!identifier) return;
    
    if (!identifierGroups[identifier]) {
      identifierGroups[identifier] = [];
    }
    identifierGroups[identifier].push({
      rowIndex: index,
      rowNumber: index + 2,
      data: row
    });
  });
  
  const duplicates = [];
  
  Object.entries(identifierGroups).forEach(([identifier, occurrences]) => {
    if (occurrences.length <= 1) return;

    const firstRow = JSON.stringify(occurrences[0].data);
    const allIdentical = occurrences.every(occ => JSON.stringify(occ.data) === firstRow);

    const assetIds = occurrences.map(occ => getAssetDetailId(occ.data));
    const allNonEmpty = assetIds.every(id => id.length > 0);
    const allUnique = new Set(assetIds).size === assetIds.length;

    const severity = (allNonEmpty && allUnique) ? 'warn' : 'block';
    const reason = (allNonEmpty && allUnique) ? 'unique-asset-id' : 'missing-asset-id';

    duplicates.push({
      identifier,
      occurrences,
      areIdentical: allIdentical,
      resolved: false,
      resolution: null,
      severity,
      reason
    });
  });
  
  return duplicates;
}

export function findMissingFields(rows, headers) {
  const issues = [];
  const requiredFields = REQUIRED_FIELDS;
  
  const missingHeaders = requiredFields.filter(field => !headers.includes(field));
  if (missingHeaders.length > 0) {
    issues.push({
      type: 'missing_headers',
      fields: missingHeaders,
      message: `Missing columns: ${missingHeaders.join(', ')}`
    });
  }
  
  requiredFields.forEach(field => {
    if (!headers.includes(field)) return;
    
    const missingRows = [];
    rows.forEach((row, index) => {
      const value = row[field];
      if (value === undefined || value === null || String(value).trim() === '') {
        missingRows.push({
          rowIndex: index,
          rowNumber: index + 2,
          field
        });
      }
    });
    
    if (missingRows.length > 0) {
      issues.push({
        type: 'missing_values',
        field,
        rows: missingRows,
        message: `${missingRows.length} row(s) missing "${field}"`
      });
    }
  });
  
  return issues;
}

export function findWhitespaceIssues(rows) {
  const issues = [];
  
  rows.forEach((row, rowIndex) => {
    SANITIZE_FIELDS.forEach(field => {
      const value = row[field];
      if (value === undefined || value === null) return;
      
      const strValue = String(value);
      const cleaned = strValue.replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').trim();
      
      if (strValue !== cleaned) {
        issues.push({
          rowIndex,
          rowNumber: rowIndex + 2,
          field,
          originalValue: strValue,
          cleanedValue: cleaned,
          hasLineBreaks: /[\r\n]/.test(strValue),
          hasExtraSpaces: strValue !== strValue.trim() || /\s{2,}/.test(strValue)
        });
      }
    });
  });
  
  return issues;
}

export function findHarmonisationIssues(rows, headers) {
  const issues = [];
  
  const HARMONISE_COLUMNS = ['sectionname', 'categoryname', 'attribute-brand', 'brand'];

  const attributeColumns = headers.filter(header =>
    header && HARMONISE_COLUMNS.includes(header.toLowerCase())
  );
  
  attributeColumns.forEach(column => {
    const valueGroups = {};
    
    rows.forEach((row, rowIndex) => {
      const value = row[column];
      if (!value) return;
      
      const strValue = String(value).trim();
      if (!strValue) return;
      
      const normalizedKey = normaliseForComparison(strValue);
      if (!normalizedKey) return;
      
      if (!valueGroups[normalizedKey]) {
        valueGroups[normalizedKey] = [];
      }
      valueGroups[normalizedKey].push({
        value: strValue,
        rowIndex,
        rowNumber: rowIndex + 2
      });
    });
    
    const normalisedKeys = Object.keys(valueGroups);
    const mergedGroups = [];
    const processedKeys = new Set();
    
    normalisedKeys.forEach(key1 => {
      if (processedKeys.has(key1)) return;
      
      const group = [...valueGroups[key1]];
      processedKeys.add(key1);
      
      normalisedKeys.forEach(key2 => {
        if (key1 === key2 || processedKeys.has(key2)) return;
        
        if (areSimilar(key1, key2)) {
          group.push(...valueGroups[key2]);
          processedKeys.add(key2);
        }
      });
      
      const uniqueValues = {};
      group.forEach(item => {
        if (!uniqueValues[item.value]) {
          uniqueValues[item.value] = [];
        }
        uniqueValues[item.value].push(item);
      });
      
      if (Object.keys(uniqueValues).length > 1) {
        const variants = Object.entries(uniqueValues).map(([value, items]) => ({
          value,
          count: items.length,
          rows: items.map(i => i.rowNumber),
          isUppercase: value === value.toUpperCase()
        }));
        
        variants.sort((a, b) => {
          if (a.isUppercase && !b.isUppercase) return -1;
          if (!a.isUppercase && b.isUppercase) return 1;
          return b.count - a.count;
        });
        
        mergedGroups.push({
          column,
          displayColumn: column.startsWith('Attribute-') ? column.substring(10) : column,
          variants,
          suggestedValue: variants[0].value,
          selectedValue: null,
          customValue: '',
          resolved: false
        });
      }
    });
    
    issues.push(...mergedGroups);
  });
  
  return issues;
}

export function validateImportData(rows, headers) {
  return {
    totalRows: rows.length,
    duplicates: findDuplicates(rows),
    missingFields: findMissingFields(rows, headers),
    whitespaceIssues: findWhitespaceIssues(rows),
    harmonisationIssues: findHarmonisationIssues(rows, headers)
  };
}

export function applyDuplicateResolution(rows, duplicates) {
  const rowsToRemove = new Set();
  
  duplicates.forEach(duplicate => {
    if (!duplicate.resolved || !duplicate.resolution) return;
    
    const match = duplicate.resolution.match(/^keep-(\d+)$/);
    if (match) {
      const keepIndex = parseInt(match[1], 10);
      duplicate.occurrences.forEach((occ, idx) => {
        if (idx !== keepIndex) {
          rowsToRemove.add(occ.rowIndex);
        }
      });
    } else if (duplicate.resolution === 'keep-first') {
      duplicate.occurrences.slice(1).forEach(occ => rowsToRemove.add(occ.rowIndex));
    } else if (duplicate.resolution === 'keep-last') {
      duplicate.occurrences.slice(0, -1).forEach(occ => rowsToRemove.add(occ.rowIndex));
    } else if (typeof duplicate.resolution === 'number') {
      duplicate.occurrences.forEach(occ => {
        if (occ.rowIndex !== duplicate.resolution) {
          rowsToRemove.add(occ.rowIndex);
        }
      });
    }
  });
  
  return rows.filter((_, index) => !rowsToRemove.has(index));
}

export function applyWhitespaceFixes(rows, issues) {
  const fixedRows = JSON.parse(JSON.stringify(rows));
  
  issues.forEach(issue => {
    if (fixedRows[issue.rowIndex]) {
      fixedRows[issue.rowIndex][issue.field] = issue.cleanedValue;
    }
  });
  
  return fixedRows;
}

export function applyHarmonisationFixes(rows, issues) {
  const fixedRows = JSON.parse(JSON.stringify(rows));
  
  issues.forEach(issue => {
    if (!issue.resolved) return;
    
    let targetValue;
    if (issue.selectedValue === '__custom__') {
      targetValue = issue.customValue;
    } else if (issue.selectedValue) {
      targetValue = issue.selectedValue;
    } else {
      return;
    }
    if (!targetValue) return;
    

    const ignoredVariants = issue.ignoredVariants || [];
    
    issue.variants.forEach(variant => {
      if (ignoredVariants.includes(variant.value)) return;
      
      variant.rows.forEach(rowNum => {
        const rowIndex = rowNum - 2;
        if (fixedRows[rowIndex]) {
          fixedRows[rowIndex][issue.column] = targetValue;
        }
      });
    });
  });
  
  return fixedRows;
}

export function exportToExcel(rows) {
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  return new Blob([excelBuffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
}

export function generateImportSummary(rows) {
  const validRows = rows.filter(row => {
    return REQUIRED_FIELDS.every(field => {
      const value = row[field];
      return value !== undefined && value !== null && String(value).trim() !== '';
    });
  });
  
  const newRecords = validRows.filter(row => {
    const assetDetailId = row.AssetDetailid || row.AssetDetailId || row.assetdetailid;
    return !assetDetailId || String(assetDetailId).trim() === '';
  });
  
  const updates = validRows.filter(row => {
    const assetDetailId = row.AssetDetailid || row.AssetDetailId || row.assetdetailid;
    return assetDetailId && String(assetDetailId).trim() !== '';
  });
  
  const sections = new Set();
  const categories = new Set();
  
  validRows.forEach(row => {
    if (row.SectionName) sections.add(String(row.SectionName).trim());
    if (row.CategoryName) categories.add(String(row.CategoryName).trim());
  });
  
  return {
    totalRows: rows.length,
    validRows: validRows.length,
    newRecords: newRecords.length,
    updates: updates.length,
    sections: Array.from(sections),
    categories: Array.from(categories)
  };
}

export default {
  parseExcelFile,
  validateTemplateFormat,
  validateImportData,
  findDuplicates,
  findMissingFields,
  findWhitespaceIssues,
  findHarmonisationIssues,
  applyDuplicateResolution,
  applyWhitespaceFixes,
  applyHarmonisationFixes,
  exportToExcel,
  generateImportSummary
};
